import React from "react";

import styled from "@emotion/styled";

import {
  CreditScoreSummary,
  CriminalRecordsType,
  EvictionsType,
} from "../interfaces";

const ReportDetailsContainer = styled.div`
  margin-bottom: 16px;
  border-radius: 4px;
  font-size: 12px;
  color: #6c757d;
`;

const ReportDetailLabel = styled.span`
  font-weight: 600;
  color: #343a40;
  min-width: 180px;
  display: inline-block;
`;

const ReportDetailValue = styled.span`
  color: #495057;
`;

type ReportDetailsProps = {
  creditScoreSummary?: CreditScoreSummary;
  criminalRecords?: CriminalRecordsType;
  evictions?: EvictionsType;
  backgroundMeta?: {
    backgroundId?: string;
    transactionId?: string;
    backgroundAndCreditCheckId?: number;
  };
};

const ReportDetails: React.FC<ReportDetailsProps> = ({
  creditScoreSummary,
  criminalRecords,
  evictions,
  backgroundMeta,
}) => {
  const transactionId = backgroundMeta?.transactionId;

  const creditCreationDate = creditScoreSummary?.creationDate;
  const creditExpirationDate = creditScoreSummary?.expirationDate;
  const creditExpired = creditScoreSummary?.expired;
  const criminalCreationDate = criminalRecords?.creationDate;
  const criminalExpirationDate = criminalRecords?.expirationDate;
  const criminalExpired = criminalRecords?.expired;
  const evictionCreationDate = evictions?.creationDate;
  const evictionExpirationDate = evictions?.expirationDate;
  const evictionExpired = evictions?.expired;

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const formatExpirationDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const formatted = new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return formatted;
    } catch {
      return dateString;
    }
  };

  const generatedOn =
    creditCreationDate || criminalCreationDate || evictionCreationDate;
  const expiresOn =
    creditExpirationDate || criminalExpirationDate || evictionExpirationDate;
  const anyExpired = creditExpired || criminalExpired || evictionExpired;

  const hasAnyData = Boolean(transactionId || generatedOn || expiresOn);

  if (!hasAnyData) return null;

  return (
    <ReportDetailsContainer>
      <div
        style={{
          padding: "8px 12px",
          marginBottom: "12px",
          backgroundColor: "#ffffff",
        }}
      >
        {transactionId && (
          <div style={{ marginBottom: "4px" }}>
            <ReportDetailLabel>Experian Report ID:</ReportDetailLabel>{" "}
            <ReportDetailValue>{transactionId}</ReportDetailValue>
          </div>
        )}
        {generatedOn && (
          <div style={{ marginBottom: "4px" }}>
            <ReportDetailLabel>Generated On:</ReportDetailLabel>{" "}
            <ReportDetailValue>{formatDate(generatedOn)}</ReportDetailValue>
          </div>
        )}
        {expiresOn && (
          <div>
            <ReportDetailLabel>
              {anyExpired ? "Expired On:" : "Expires On:"}
            </ReportDetailLabel>{" "}
            <ReportDetailValue>
              {formatExpirationDate(expiresOn)}
            </ReportDetailValue>
          </div>
        )}
      </div>
    </ReportDetailsContainer>
  );
};

export default ReportDetails;
